import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  CardContent, 
  Typography, 
  Button
} from '@mui/material';
import { 
  Assignment as WorklistIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { WorklistGrid } from '../components/grids';
import { WORKLIST_TYPES, WorklistType } from '../../../services/CaseManagementService';

const CasemanWorklist: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const type = searchParams.get('type') || '';
  const { handleCenterChange } = useCenterSelection();

  const [refreshKey, setRefreshKey] = useState(0);

  // Validate type parameter
  const selectedType: WorklistType = WORKLIST_TYPES.includes(type as WorklistType) 
    ? (type as WorklistType) 
    : 'Clinical Review';

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (!company || !type) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard sx={{ maxWidth: 800 }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                Missing required parameters. Please specify company and type to view worklist.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                Company: {company || 'Not specified'} | Type: {type || 'Not specified'}
              </Typography>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 20
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WorklistIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1e293b',
                    mb: 0.5
                  }}
                >
                  {selectedType} Worklist
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>{selectedType}</Typography>
              </Box>
            </Box>
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ 
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: '#2563eb',
                    backgroundColor: '#eff6ff'
                  },
                  fontWeight: 600
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />
            </Box>
            {/* Worklist Grid */}
            <WorklistGrid key={refreshKey} />
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default CasemanWorklist;
